import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AccountPage() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  if (user === null) {
    return (
      <Layout>
        <div className="max-w-[960px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="mono text-xs text-neutral-500">Loading&hellip;</p>
        </div>
      </Layout>
    );
  }
  if (!user || user === false) {
    return (
      <Layout>
        <div className="max-w-[960px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="text-neutral-700">
            <Link to="/login" className="edi-link">Sign in</Link> to manage your account.
          </p>
        </div>
      </Layout>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <SEO path="/account" title="Your account" noindex description="Manage your account on What Do We Want? When Do We Want It?" />
      <section className="max-w-[960px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-700">Account</div>
        <h1 className="serif text-5xl mt-2 tracking-tight">Your account</h1>
        <p className="text-neutral-700 mt-3">
          Signed in as <strong>{user.name}</strong> ({user.email}) &middot;{" "}
          <span className="mono text-xs uppercase tracking-[0.2em] text-amber-700">{user.role}</span>
        </p>

        <form onSubmit={submit} className="mt-10 border border-black p-6 space-y-6 max-w-lg" data-testid="account-password-form">
          <div>
            <h2 className="serif text-2xl">Change password</h2>
            <p className="text-sm text-neutral-600 mt-1">
              You&rsquo;ll stay signed in on this device after the change.
            </p>
          </div>

          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Current password</label>
            <input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              data-testid="account-current-password"
              className="mt-2 w-full border-b border-neutral-400 focus:border-black bg-transparent py-2 focus:outline-none"
            />
          </div>

          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              New password (min 8 characters)
            </label>
            <input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={8}
              data-testid="account-new-password"
              className="mt-2 w-full border-b border-neutral-400 focus:border-black bg-transparent py-2 focus:outline-none"
            />
          </div>

          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Confirm new password</label>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              data-testid="account-confirm-password"
              className="mt-2 w-full border-b border-neutral-400 focus:border-black bg-transparent py-2 focus:outline-none"
            />
          </div>

          <div className="flex justify-end pt-2 border-t border-neutral-200">
            <button
              type="submit"
              disabled={saving}
              data-testid="account-submit-button"
              className="bg-black text-white px-6 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-amber-700 disabled:opacity-40 transition-colors"
            >
              {saving ? "Updating…" : "Update password"}
            </button>
          </div>
        </form>
      </section>
    </Layout>
  );
}
